import { UserLeadSettings } from '@cutr/constants/cutlist';
import {
  Position,
  WindowAddress,
  WindowOrder,
  WindowOrderStatus,
} from '@cutr/constants/windows/windows';

import { ApiError, logError } from '@/api/backend';
import { PaginatedData } from '@/api/backend/agent';
import { useAuthStore } from '@/api/login';
import i18n from '@/i18n';
import { i18nInit } from '@/i18n';
import { getTicker } from '@/theme';

import { getFilenameFromDisposition } from './apiUtils';

const baseUrl = import.meta.env.VITE_API_BASE_URL;

export type GetOrdersParams = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  statuses?: WindowOrderStatus[];
  createdAfter?: string;
  createdBefore?: string;
  submittedAfter?: string;
  submittedBefore?: string;
};

const checkError = async (res: Response) => {
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));

    throw new ApiError(json.message || res.statusText, res.status, json);
  }

  return res;
};

export const windowApi = {
  createBaseUrl(resource = '', basePath = '') {
    return new URL(resource, baseUrl + basePath);
  },
  createUrl(path = '') {
    const url = this.createBaseUrl(path, '/v2/windows/');

    return url;
  },
  async getHeaders({ includeAuth = true, isMultipart = false } = {}): Promise<
    Record<string, string>
  > {
    const auth = useAuthStore.getState();

    const ticker = getTicker();
    const headers: Record<string, string> = {
      'X-Cutlist-Source': ticker,
    };

    // Only set Content-Type for non-multipart requests
    if (!isMultipart) {
      headers['Content-Type'] = 'application/json';
    }

    await i18nInit;
    if (i18n.resolvedLanguage) {
      headers['Accept-Language'] = i18n.resolvedLanguage;
    }

    if (includeAuth && auth.token) {
      headers['Authorization'] = `Bearer ${auth.token}`;
    }

    return headers;
  },
  createOrder: async (title: string): Promise<WindowOrder> => {
    const body = JSON.stringify({ title });
    const headers = await windowApi.getHeaders();
    return fetch(windowApi.createUrl('orders'), {
      headers,
      body,
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getOrder: (orderId: string): Promise<WindowOrder> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createUrl(`orders/${orderId}`), {
          headers,
          credentials: 'include',
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  patchOrder: (
    orderId: string,
    data: Partial<WindowOrder>
  ): Promise<WindowOrder> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createUrl(`orders/${orderId}`), {
          headers,
          credentials: 'include',
          method: 'PATCH',
          body: JSON.stringify(data),
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  submitOrder: (
    orderId: string,
    base64Images: Record<Position['id'], string>
  ): Promise<WindowOrder> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createUrl(`orders/${orderId}/submit`), {
          headers,
          credentials: 'include',
          method: 'POST',
          body: JSON.stringify({ images: base64Images }),
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  placeOrder: (orderId: string): Promise<WindowOrder> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createUrl(`orders/${orderId}/place-order`), {
          headers,
          credentials: 'include',
          method: 'POST',
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  async getAllOrders(
    params: GetOrdersParams
  ): Promise<PaginatedData<WindowOrder>> {
    const urlParams = new URLSearchParams({
      pageIndex: params.pageIndex.toString(),
      pageSize: params.pageSize.toString(),
    });

    for (const k of [
      'search',
      'statuses',
      'createdAfter',
      'createdBefore',
      'submittedAfter',
      'submittedBefore',
    ]) {
      const key = k as keyof GetOrdersParams;
      if (params[key]) {
        urlParams.set(key, params[key] as string);
      }
    }

    const headers = await windowApi.getHeaders();

    return fetch(windowApi.createUrl(`orders?${urlParams.toString()}`), {
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data, totalPages, totalRows }) => ({
        rows: data.orders,
        totalPages,
        totalRows,
      }))
      .catch(logError);
  },

  // Delivery Address
  updateOrderDeliveryAddress: async (
    orderId: string,
    address: WindowAddress
  ) => {
    const headers = await windowApi.getHeaders();
    return fetch(windowApi.createUrl(`orders/${orderId}/delivery-address`), {
      headers,
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify(address),
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },

  // Positions
  createPosition: async (
    orderId: string,
    data: Partial<Position>
  ): Promise<{
    position: Position;
    orderPricingLineItems: WindowOrder['pricingLineItems'];
  }> => {
    const body = JSON.stringify(data);

    const headers = await windowApi.getHeaders();
    return fetch(windowApi.createUrl(`orders/${orderId}/positions`), {
      headers,
      body,
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getPosition: (orderId: string, positionId: string): Promise<Position> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(
          windowApi.createUrl(`orders/${orderId}/positions/${positionId}`),
          {
            headers,
            credentials: 'include',
          }
        )
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getPositions: (orderId: string): Promise<Position[]> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createUrl(`orders/${orderId}/positions`), {
          headers,
          credentials: 'include',
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  patchPosition: (
    orderId: string,
    positionId: string,
    data: Partial<Position>
  ): Promise<{
    position: Position;
    orderPricingLineItems: WindowOrder['pricingLineItems'];
  }> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(
          windowApi.createUrl(`orders/${orderId}/positions/${positionId}`),
          {
            headers,
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify(data),
          }
        )
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  deletePosition: (
    orderId: string,
    positionId: string
  ): Promise<{ orderPricingLineItems: WindowOrder['pricingLineItems'] }> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(
          windowApi.createUrl(`orders/${orderId}/positions/${positionId}`),
          {
            headers,
            credentials: 'include',
            method: 'DELETE',
          }
        )
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  duplicatePosition: async (
    orderId: string,
    positionId: string,
    data: Partial<Position>
  ): Promise<{
    position: Position;
    orderPricingLineItems: WindowOrder['pricingLineItems'];
  }> => {
    const body = JSON.stringify(data);
    const headers = await windowApi.getHeaders();
    return fetch(
      windowApi.createUrl(
        `orders/${orderId}/positions/${positionId}/duplicate`
      ),
      {
        headers,
        body,
        credentials: 'include',
        method: 'POST',
      }
    )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },

  getOrderPDF: (
    orderId: string,
    images: Record<Position['id'], string>
  ): Promise<{ data: Blob; filename: string }> => {
    const body = JSON.stringify({
      images,
    });

    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createUrl(`orders/${orderId}/downloads/pdf`), {
          method: 'POST',
          headers,
          credentials: 'include',
          body,
        })
      )
      .then(checkError)
      .then(async (res) => {
        const blob = await res.blob();

        const filename = getFilenameFromDisposition(
          res.headers,
          `order-${orderId}.pdf`
        );

        return { data: blob, filename };
      })
      .catch(logError);
  },

  updateOrderLogo: async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('logo', file);

    return windowApi
      .getHeaders({ isMultipart: true })
      .then((headers) =>
        fetch(windowApi.createBaseUrl('', '/v2/cutlist/settings/logo'), {
          headers,
          credentials: 'include',
          method: 'POST',
          body: formData,
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },

  deleteOrderLogo: async (): Promise<void> => {
    return windowApi
      .getHeaders()
      .then((headers) =>
        fetch(windowApi.createBaseUrl('', '/v2/cutlist/settings/logo'), {
          headers,
          credentials: 'include',
          method: 'DELETE',
        })
      )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },

  getOrderSettings: async (): Promise<UserLeadSettings> => {
    const headers = await windowApi.getHeaders();
    return fetch(windowApi.createBaseUrl('', '/v2/cutlist/settings'), {
      headers,
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
};
